import { appConfig } from "@/config";
import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { PreAuthToken, Token } from "@repo/schema";
import { clearCredentials, setCredentials, setPreAuthCredentials } from "../auth/services/authActions";
import type { AuthState } from "../auth/types";
import i18n from "@/core/i18n";
import { ACCESS_TOKEN_KEY } from "./constants";

export const baseQuery = fetchBaseQuery({
  baseUrl: appConfig.studioApi.baseUrl,
  prepareHeaders: (headers) => {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const contentType = headers.get("Content-Type");
    if (!contentType) {
      headers.set("Content-Type", "application/json");
    }
    // Read by the API (`core/i18n`): emails fall back to it and LLM prompts
    // name it as the language to write in.
    headers.set("Accept-Language", i18n.language);

    return headers;
  },
  credentials: "include",
});

let refreshPromise: Promise<boolean> | null = null;

// Endpoints where a 401 is the real answer rather than an expired token. The
// API deliberately uses 400 for business-level failures so every other 401 can
// be treated as "refresh and retry" (see AuthService.changePassword). Login and
// refresh are the exception: refreshing on their 401 would let a leftover
// refresh cookie silently restore the previous session, and the rest are public
// endpoints where a refresh attempt is pointless.
const { auth } = appConfig.studioApi.endpoints;
const credentialEndpoints = [
  auth.login,
  auth.refreshToken,
  auth.register,
  auth.forgotPassword,
  auth.resetPassword,
  auth.verifyEmail,
  auth.resendVerification,
];

const isCredentialEndpoint = (args: string | FetchArgs) => {
  const url = typeof args === "string" ? args : args.url;
  return credentialEndpoints.some((endpoint) => url.startsWith(endpoint));
};

/**
 * Swap the refresh cookie for a fresh access token. Resolves false when the
 * session is gone, having cleared the credentials.
 *
 * Extracted from `baseRefreshingQuery` so the NDJSON streaming transport can
 * recover from a 401 too (see `streamNdjson`). Streaming responses cannot go
 * through `fetchBaseQuery`, but they must not each grow their own copy of this
 * — the shared `refreshPromise` is what stops a stream 401 and a concurrent
 * RTK 401 racing two refreshes against a single-use cookie.
 */
export const refreshSession = async (
  api: BaseQueryApi,
  extraOptions: object = {},
): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshResult = await baseQuery(
        {
          url: appConfig.studioApi.endpoints.auth.refreshToken,
          method: "POST",
        },
        api,
        extraOptions,
      );
      if (refreshResult.data) {
        const data = refreshResult.data as Token | PreAuthToken;
        // The refresh cookie is shared by every tab and outlives a login, so
        // a refresh can come back for a different user than the one this app
        // is logged in as. Adopting it would silently swap sessions — never
        // trust it over the user we already have.
        const currentUserId = (api.getState() as { auth?: AuthState }).auth?.user?.id;
        if (currentUserId !== undefined && data.user.id !== currentUserId) {
          api.dispatch(clearCredentials());
          return false;
        }
        if ('organization' in data) {
          api.dispatch(setCredentials(data as Token));
        } else {
          api.dispatch(setPreAuthCredentials(data as PreAuthToken));
        }
        return true;
      } else {
        api.dispatch(clearCredentials());
        return false;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

export const baseRefreshingQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  // Deliberately not gated on an access token already being in sessionStorage:
  // sessionStorage is per-tab, so a tab opened into an existing session starts
  // empty. Gating here left any 401 raised before the token lands unretried —
  // the request failed silently and only a manual reload recovered it.
  if (result.error && result.error.status === 401 && !isCredentialEndpoint(args)) {
    if (await refreshSession(api, extraOptions ?? {})) {
      return baseQuery(args, api, extraOptions);
    }
  }

  return result;
};
